import ToneChip from './tone-chip';
import MetaItem from './meta-item';
import { FreshdeskTicket } from 'library/types';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FormattedMessage, useIntl } from 'react-intl';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import { Box, Stack, Avatar, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { formatDateTime, getDueTone, getInitials, getPriorityMeta, getStatusMeta } from '../utils';

type TicketListItemProps = {
  ticket: FreshdeskTicket;
};

const TicketListItem = ({ ticket }: TicketListItemProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const status = getStatusMeta(ticket.status, intl, ticket.statusText);
  const priority = getPriorityMeta(ticket.priority, intl);

  return (
    <Box
      component="a"
      href={ticket.ticketUrl}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'block',
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.03) : '#fff',
        color: 'inherit',
        textDecoration: 'none',
        transition: 'border-color 0.2s ease, background-color 0.2s ease',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.45),
          bgcolor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.08)
              : alpha(theme.palette.primary.main, 0.035),
        },
      }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              <FormattedMessage id="freshdesk.ticketNumber" values={{ id: ticket.id }} />
            </Typography>
            <ToneChip label={status.label} tone={status.tone} />
            <ToneChip label={priority.label} tone={priority.tone} />
          </Stack>

          <Tooltip title={ticket.subject} placement="top-start">
            <Typography
              variant="subtitle1"
              sx={{
                mb: 1.25,
                fontWeight: 800,
                lineHeight: 1.35,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}>
              {ticket.subject}
            </Typography>
          </Tooltip>

          <Stack direction="row" flexWrap="wrap" gap={1.5}>
            <MetaItem
              icon={<AccessTimeIcon />}
              label={intl.formatMessage(
                { id: 'freshdesk.ticket.createdAt' },
                { date: formatDateTime(ticket.createdAt, intl) }
              )}
            />
            <MetaItem
              icon={<RefreshIcon />}
              label={intl.formatMessage(
                { id: 'freshdesk.ticket.updatedAt' },
                { date: formatDateTime(ticket.updatedAt, intl) }
              )}
            />
            <ToneChip
              icon={<AssignmentLateIcon />}
              tone={getDueTone(ticket.dueBy)}
              label={intl.formatMessage({ id: 'freshdesk.ticket.dueBy' }, { date: formatDateTime(ticket.dueBy, intl) })}
            />
            {ticket.closedAt && (
              <MetaItem
                icon={<CheckCircleIcon />}
                label={intl.formatMessage(
                  { id: 'freshdesk.ticket.closedAt' },
                  { date: formatDateTime(ticket.closedAt, intl) }
                )}
              />
            )}
          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: { xs: 0, md: 220 }, alignSelf: { xs: 'stretch', md: 'center' } }}>
          <Avatar sx={{ width: 36, height: 36, fontSize: 13, bgcolor: alpha(theme.palette.primary.main, 0.9) }}>
            {getInitials(ticket.responderName)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
              {ticket.responderName || intl.formatMessage({ id: 'freshdesk.unassigned' })}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {ticket.responderEmail || intl.formatMessage({ id: 'freshdesk.notAvailable' })}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

export default TicketListItem;
